import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '../admin/firebase/config';

const OutletContext = createContext();

export const useOutlet = () => {
  const context = useContext(OutletContext);
  if (!context) {
    throw new Error('useOutlet must be used within OutletProvider');
  }
  return context;
};

export const OutletProvider = ({ children }) => {
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'all'
  const [loading, setLoading] = useState(true);

  // Load all active outlets from Firebase and restore view mode
  useEffect(() => {
    console.log('🏢 Loading outlets...');

    // Restore view mode from localStorage
    const savedViewMode = localStorage.getItem('outletViewMode');
    if (savedViewMode === 'all' || savedViewMode === 'single') {
      setViewMode(savedViewMode);
    }

    const q = query(
      collection(db, 'outlets'),
      where('isActive', '==', true),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const outletsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        console.log(`📊 Loaded ${outletsData.length} outlets:`, outletsData.map(o => o.name));
        setOutlets(outletsData);

        // Auto-select outlet on initial load
        if (!selectedOutlet && outletsData.length > 0) {
          // Try to restore from localStorage
          const savedOutletId = localStorage.getItem('selectedOutletId');
          const savedOutlet = outletsData.find(o => o.id === savedOutletId);

          if (savedOutlet) {
            console.log('✅ Restored outlet from localStorage:', savedOutlet.name);
            setSelectedOutlet(savedOutlet);
          } else {
            // Default to first outlet
            console.log('✅ Auto-selected first outlet:', outletsData[0].name);
            setSelectedOutlet(outletsData[0]);
            localStorage.setItem('selectedOutletId', outletsData[0].id);
          }
        }

        setLoading(false);
      },
      (error) => {
        console.error('❌ Error loading outlets:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []); // Only run on mount

  // Update selected outlet when outlets data changes
  useEffect(() => {
    if (selectedOutlet && outlets.length > 0) {
      const updatedOutlet = outlets.find(o => o.id === selectedOutlet.id);
      if (updatedOutlet && JSON.stringify(updatedOutlet) !== JSON.stringify(selectedOutlet)) {
        console.log('🔄 Outlet data updated:', updatedOutlet.name);
        setSelectedOutlet(updatedOutlet);
      }
    }
  }, [outlets, selectedOutlet]);

  // Select outlet and persist to localStorage
  const selectOutlet = (outletId) => {
    const outlet = outlets.find(o => o.id === outletId);
    if (outlet) {
      console.log('🏢 Switching to outlet:', outlet.name);
      setSelectedOutlet(outlet);
      localStorage.setItem('selectedOutletId', outletId);
    } else {
      console.warn('⚠️ Outlet not found:', outletId);
    }
  };

  // Get outlet by ID
  const getOutletById = (outletId) => {
    return outlets.find(o => o.id === outletId);
  };

  // Check if user has access to outlet (can be extended with role-based logic)
  const hasAccessToOutlet = (outletId, userRole = 'admin') => {
    // Admins and managers have access to all outlets
    if (userRole === 'admin' || userRole === 'manager') {
      return true;
    }
    // For other roles, check if outlet exists and is active
    const outlet = getOutletById(outletId);
    return outlet && outlet.isActive;
  };

  // Toggle view mode between single outlet and all outlets
  const toggleViewMode = () => {
    const newMode = viewMode === 'single' ? 'all' : 'single';
    console.log('🔄 Switching view mode to:', newMode);
    setViewMode(newMode);
    localStorage.setItem('outletViewMode', newMode);
  };

  // Set view mode explicitly
  const setOutletViewMode = (mode) => {
    console.log('🔄 Setting view mode to:', mode);
    setViewMode(mode);
    localStorage.setItem('outletViewMode', mode);
  };

  const value = {
    outlets,
    selectedOutlet,
    selectOutlet,
    getOutletById,
    hasAccessToOutlet,
    loading,
    viewMode,
    toggleViewMode,
    setOutletViewMode
  };

  return (
    <OutletContext.Provider value={value}>
      {children}
    </OutletContext.Provider>
  );
};
